'use client';

import { css } from '@bts-web/utils-style-engine';
import { FC } from 'react';
import { getAppConfig } from '@bts-web/core-app-config';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { HTMLRenderer } from '../../../../common';
import { RegularWarning } from '../../../../common/svgIcons';
import { useFeatureFlag } from '../../../../statsig/sdk/provider';
import { AutomaticWithdrawalsToggle } from './AutomaticWithdrawalsToggle/AutomaticWithdrawalsToggle.use-client';

const warningTextStyle = css({
  color: 'warning.text_primary',
  fontSize: 'caption.small',
  lineHeight: 'caption.small',
  letterSpacing: 'caption.small',
  gap: 'extra_small_2',
  maxWidth: '200px',
  display: 'flex',
  alignItems: 'center',
});

const successSellSubtitlesStyles = css({
  display: 'flex',
  flexDirection: 'column',
  fontSize: 'body.medium',
  gap: 'extra_small',
  color: 'neutrals.text_secondary',
  width: '100%',
  '& [data-element="markdown-style"]': {
    color: 'neutrals.text_primary',
  },
  '& [data-element="additional-text-style"]': {
    color: 'neutrals.text_primary',
  },
});

const taxWithHoldingStatusStyles = css({
  paddingTop: 'extra_small_3',
  paddingInlineEnd: 'extra_small_2',
  paddingBottom: 'extra_small_3',
  paddingInlineStart: 'extra_small_2',
  fontSize: 'caption.medium_medium',
  fontWeight: 'caption.medium_medium',
  lineHeight: 'caption.medium_medium',
  letterSpacing: 'caption.medium_medium',
  bg: 'neutrals.fill_quinary',
  color: 'neutrals.text_primary',
  borderRadius: '32px',
});

export const SuccessSellContent: FC<{
  hasTaxOfflineWarning?: boolean;
  warningTaxNotFinalMessage?: string;
  subtitleMessage: string;
  taxWithheldMessage?: string;
}> = ({
  subtitleMessage,
  hasTaxOfflineWarning,
  warningTaxNotFinalMessage,
  taxWithheldMessage,
}) => {
  const { t } = useClientTranslation();

  const feature_enableFiatTransfers = useFeatureFlag(
    'feature_enableFiatTransfers',
  );

  const { additionalSellConfirmationText } = getAppConfig();

  const lbbwAdditionalLine = additionalSellConfirmationText
    ? t(additionalSellConfirmationText as any)
    : null;

  return (
    <div
      className={successSellSubtitlesStyles}
      data-testid="sell-success-content"
    >
      <div
        className={css({
          gap: 'medium',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        })}
      >
        <h1
          className={css({
            '& [data-element="markdown-style"]': {
              color: 'neutrals.text_primary',
              whiteSpace: 'nowrap',
            },
          })}
        >
          <HTMLRenderer>{subtitleMessage}</HTMLRenderer>
        </h1>

        {lbbwAdditionalLine ? (
          <span data-element="additional-text-style">
            <HTMLRenderer>{lbbwAdditionalLine}</HTMLRenderer>
          </span>
        ) : null}

        {taxWithheldMessage && (
          <p className={taxWithHoldingStatusStyles}>{taxWithheldMessage}</p>
        )}
      </div>

      {feature_enableFiatTransfers && <AutomaticWithdrawalsToggle />}

      {hasTaxOfflineWarning && (
        <div className={warningTextStyle}>
          <RegularWarning size={13} />
          {warningTaxNotFinalMessage}
        </div>
      )}
    </div>
  );
};
